import type { CarouselProps } from "antd";
import { Carousel } from "antd";
import type { CarouselRef } from "antd/es/carousel";
import classNames from "classnames";
import React, {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./CORECarousel.less";

export type CORECarouselImageItem = {
  key?: React.Key;
  src?: string;
  alt?: string;
  imagePlaceholder?: string;
};

export type CORECarouselNodeItem = {
  key?: React.Key;
  node: ReactNode;
};

export type CORECarouselItem = CORECarouselImageItem | CORECarouselNodeItem;

export interface CORECarouselProps extends CarouselProps {
  items: CORECarouselItem[];
  contentOverlay?: ReactNode;
  showHoverOverlay?: boolean;
  height?: number;
  /** Border radius in pixels [top-left, top-right, bottom-right, bottom-left] */
  borderRadius?: [number, number, number, number] | number;
  wrapperClassName?: string;
  wrapperStyle?: CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
}

const clickZone = "core-carousel__click-zone";

const rateLimitMaxRetries = 3;

const CORECarouselImage: React.FC<{
  item: CORECarouselImageItem;
  slideStyle?: CSSProperties;
}> = ({ item, slideStyle }) => {
  const [retryNonce, setRetryNonce] = useState(0);
  const [failed, setFailed] = useState(false);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(retryTimerRef.current), []);

  const handleError = async () => {
    // already showing the placeholder (or nothing better to show) — give up
    if (failed || !item.src) return;

    if (retryCountRef.current < rateLimitMaxRetries) {
      try {
        const probe = await fetch(item.src, {
          method: "HEAD",
          credentials: "include",
          cache: "no-store",
        });
        if (probe.status === 429) {
          const retryAfterSeconds =
            Number(probe.headers.get("retry-after")) || 1;
          const delayMs =
            retryAfterSeconds * 1000 * 2 ** retryCountRef.current +
            Math.random() * 250; // jitter so parallel images don't re-burst together
          retryCountRef.current += 1;
          console.warn(
            `Image request was rate limited (429); retry ${
              retryCountRef.current
            }/${rateLimitMaxRetries} in ${Math.round(delayMs)}ms: ${item.src}`
          );
          retryTimerRef.current = window.setTimeout(
            () => setRetryNonce((nonce) => nonce + 1),
            delayMs
          );
          return;
        }
      } catch {
        // probe failed (network/CORS) — treat as a permanent error
      }
    }
    setFailed(true);
  };

  return (
    <img
      key={retryNonce}
      style={slideStyle}
      className="core-carousel__image"
      loading="lazy"
      src={
        (failed ? item.imagePlaceholder : item.src) ??
        item.imagePlaceholder ??
        ""
      }
      alt={item.alt ?? ""}
      onError={handleError}
    />
  );
};

export const CORECarousel = forwardRef<CarouselRef, CORECarouselProps>(
  (
    {
      items,
      contentOverlay,
      showHoverOverlay = true,
      height,
      borderRadius = [4, 4, 4, 4],
      wrapperClassName,
      wrapperStyle,
      className,
      dots = true,
      style,
      onClick,
      disabled = false,
      ...rest
    },
    forwardedRef
  ) => {
    const internalRef = useRef<CarouselRef | null>(null);
    const hasMultipleItems = items.length > 1;

    const setRefs = useCallback(
      (node: CarouselRef | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<CarouselRef | null>).current =
            node;
        }
      },
      [forwardedRef]
    );

    const handleSlideNavigation = useCallback(
      (direction: "prev" | "next", e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        internalRef.current?.[direction]();
      },
      []
    );

    const slideStyle = height ? { height: `${height}px` } : undefined;
    const resolvedWrapperStyle = {
      ...wrapperStyle,
      ...(borderRadius && {
        borderRadius: Array.isArray(borderRadius)
          ? `${borderRadius[0]}px ${borderRadius[1]}px ${borderRadius[2]}px ${borderRadius[3]}px`
          : `${borderRadius}px`,
      }),
    };

    const resolvedDots = useMemo(
      () => (hasMultipleItems ? dots : false),
      [dots, hasMultipleItems]
    );

    const handleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
      const target = event.target as HTMLElement;

      if (target.closest(".slick-dots") || target.closest(`.${clickZone}`)) {
        event.stopPropagation();
        return;
      }

      if (!disabled && onClick) {
        onClick(event);
      }
    };

    return (
      <div
        className={classNames("core-carousel", wrapperClassName, {
          "core-carousel--single": !hasMultipleItems,
          "core-carousel--disabled": disabled,
        })}
        style={resolvedWrapperStyle}
        onClick={handleClick}
        aria-disabled={disabled}
      >
        {hasMultipleItems && !disabled && (
          <>
            <div
              className={`${clickZone} ${clickZone}--left`}
              onClick={(e) => handleSlideNavigation("prev", e)}
              aria-label="Previous slide"
              role="button"
            />
            <div
              className={`${clickZone} ${clickZone}--right`}
              onClick={(e) => handleSlideNavigation("next", e)}
              aria-label="Next slide"
              role="button"
            />
          </>
        )}
        <Carousel
          ref={setRefs}
          className={classNames("core-carousel__slider", className)}
          dots={resolvedDots}
          style={style}
          {...rest}
        >
          {items.map((item, index) => {
            const key = item.key ?? index;

            if ("node" in item) {
              return (
                <div
                  key={key}
                  className="core-carousel__slide"
                  style={slideStyle}
                >
                  {item.node}
                </div>
              );
            }

            return (
              <div key={key} className="core-carousel__slide">
                <CORECarouselImage item={item} slideStyle={slideStyle} />
              </div>
            );
          })}
        </Carousel>
        {showHoverOverlay && (
          <div className="core-carousel__hover-overlay" aria-hidden />
        )}
        {contentOverlay && (
          <div className="core-carousel__content-overlay">{contentOverlay}</div>
        )}
      </div>
    );
  }
);

CORECarousel.displayName = "CORECarousel";
